import { formatPrice } from 'Shared/utils';
import { Fee } from 'QuoteApp/services/endpoints/FeeStrategy';
import {
  PaymentPlanType,
  PaymentScheduleType,
} from 'QuoteApp/services/endpoints/GetPaymentDetails';
import {
  getPaymentScheduleItemsByType,
  getPaymentScheduleEntry,
  toTitleCase,
  percentStr,
  isFLInstallmentFee,
} from 'QuoteApp/utils/feeStrategy';

type FeeItem = PaymentScheduleType['items'][number];
type FeeLine = { label: string; amount: string };

// Build a normalized list of fee lines by doing searching
// the PaymentSchedule items in the FeeStrategy fees
// - If a FeeItem.name matches a Fee.name, use the Fee.displayName as label
// - Otherwise, Title-Case the FeeItem.name as label
// - amount is the formatted amount via formatPrice
// Ordering: non-found items first (Title-Cased), then found items (using displayName)
export const buildFeeLines = (feeItems: FeeItem[], fees: Fee[]): FeeLine[] => {
  const feeByName = new Map<string, Fee>(
    fees.map((f) => [f.name.toLowerCase(), f])
  );

  const notFound: FeeLine[] = [];
  const found: FeeLine[] = [];

  for (const item of feeItems) {
    const match = feeByName.get(item.name.toLowerCase());
    const amount = formatPrice(item.amount);

    if (!match) {
      notFound.push({ label: toTitleCase(item.name), amount });
    } else {
      found.push({ label: match.displayName, amount });
    }
  }

  return [...notFound, ...found];
};

export const buildDownPaymentFeeLines = (paymentPlan: PaymentPlanType) => {
  const downPayment = getPaymentScheduleEntry(
    paymentPlan.paymentSchedule,
    'DOWNPAYMENT'
  );

  const feeItems = getPaymentScheduleItemsByType(
    downPayment?.items || [],
    'FEE'
  );

  return buildFeeLines(feeItems, paymentPlan.feeStrategy.fees);
};

export const buildInstallmentFeeLines = (paymentPlan: PaymentPlanType) => {
  const installment = getPaymentScheduleEntry(
    paymentPlan.paymentSchedule,
    'INSTALLMENT'
  );

  const feeItems = getPaymentScheduleItemsByType(
    installment?.items || [],
    'FEE'
  );

  // Exclude FL item from general fee lines to avoid duplication
  const generalItems = feeItems.filter(
    (feeItem) => !isFLInstallmentFee(feeItem.name)
  );

  // Build FL-specific lines
  const flLines = buildInstallmentFLLines(
    feeItems,
    paymentPlan.feeStrategy.fees
  );

  return [
    ...buildFeeLines(generalItems, paymentPlan.feeStrategy.fees),
    ...flLines,
  ];
};

const buildInstallmentFLLines = (
  feeItems: FeeItem[],
  fees: Fee[]
): FeeLine[] => {
  const hasFLFeeItem = feeItems.some((i) => isFLInstallmentFee(i.name));

  if (!hasFLFeeItem) return [];

  const fee = fees.find((f) => isFLInstallmentFee(f.name));

  if (!fee) return [];

  const charge = fee.charge as Fee['charge'];

  if (charge?.type === 'PERCENT') {
    const percentAmount = Number.isFinite(charge.percent) ? charge.percent : 0;
    return [
      {
        label: 'Installment Fee',
        amount: `${percentStr(percentAmount)} simple interest`,
      },
    ];
  }

  if (charge?.type === 'FLAT') {
    const amt = Number.isFinite(charge.amount) ? charge.amount : 0;
    return [
      {
        label: 'Installment Fee',
        amount: `${formatPrice(amt, 0)} simple interest`,
      },
    ];
  }

  return [];
};
